import React, {useEffect, useState} from "react";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle} from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {resourcePlanDefault} from "../../../../defaults/Items";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  restoreFormData
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import Toggler from "../../../elements/Toggler";
import QTimePicker from "../../../elements/QTimePicker";
import ResourcePlanItemsCard from "../resource_plan_item/ResourcePlanItemsCard";
import ModalControlsSaveOnly from "../../common/ModalControlsSaveOnly";
import useAuth from "../../../../hooks/useAuth";
import {ToastContainer} from "react-toastify";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import '../../../../css/ResourcePlan.css';
import '../../../../css/ResourceForms.css';

const periodTypeLabels = {
  DAILY: "Dzienny",
  WEEKEND: "Weekendowy",
  WEEKLY: "Tygodniowy",
  BIWEEKLY: "Dwutygodniowy",
  MONTHLY: "Miesięczny",
  CUSTOM: "Dowolny okres"
};

const fixedPeriodDays = {DAILY: 1, WEEKEND: 2, WEEKLY: 7, BIWEEKLY: 14, MONTHLY: 31};

const ResourcePlan = () => {
  const dataUrl = "/resource-plans";
  const positionsUrl = "/schedule-access/positions";
  const departmentsUrl = "/schedule-access/departments";
  const defaultItem = resourcePlanDefault;
  const itemName = "plan zapotrzebowania";
  const itemNames = "plany zapotrzebowania";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);
  const {auth} = useAuth();

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [positionsList, setPositionsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [workStart, setWorkStart] = useState(null);
  const [workEnd, setWorkEnd] = useState(null);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Okres", accessor: "periodType", sortable: true, searchable: true, format: (value) => periodTypeLabels[value] || "Dzienny"},
    {label: "Aktywny", accessor: "active", sortable: true, searchable: false, type: "bool"},
    {label: "Start pracy", accessor: "workStart", sortable: true, searchable: true},
    {label: "Koniec pracy", accessor: "workEnd", sortable: true, searchable: true},
    {label: "Liczba osób", accessor: "totalStaff", sortable: true, searchable: true},
    {label: "Liczba godzin", accessor: "totalHours", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const parseTimeToDate = (timeString) => {
    if (!timeString) return null;
    if (typeof timeString === "string") {
      const [hours, minutes] = timeString.split(":").map(Number);
      return new Date(1970, 0, 1, hours || 0, minutes || 0, 0);
    }
    if (timeString instanceof Date) return timeString;
    return null;
  };

  const formatToLocalTime = (value) => {
    if (!value) return null;
    if (typeof value === "string" && /^([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(value)) {
      return value.slice(0, 5);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
    }
    return null;
  };

  const positionNames = (item) => {
    const positions = Array.isArray(item.positions) ? item.positions : [];
    if (positions.length === 0) return "dowolne stanowisko w dziale";
    return positions.map((position) => position.name || `#${position.id}`).join(", ");
  };

  const describePlanItem = (item) => {
    const departmentLabel = item.department?.name || `dział #${item.department?.id || "?"}`;
    return `dzień ${item.dayIndex || 1}, ${departmentLabel}, ${positionNames(item)}`;
  };

  const responseMessage = (response) => {
    const data = response?.data;
    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (data?.detail) return data.detail;
    if (data?.error) return data.error;
    if (Array.isArray(data?.errors)) {
      return data.errors.map((error) => error?.message || error?.defaultMessage || String(error)).join("; ");
    }
    return "";
  };

  const showSaveError = (message) => {
    setSaveFeedback({type: "danger", message});
    toastError(message);
    window.requestAnimationFrame(() => {
      document.getElementById("resource-plan-save-feedback")?.scrollIntoView({behavior: "smooth", block: "center"});
    });
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403 || response?.status === 401 || response?.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
    }
  };

  const preparePlanForSave = () => {
    const planWorkStart = formatToLocalTime(currentItem.workStart);
    const planWorkEnd = formatToLocalTime(currentItem.workEnd);
    const maxDayIndex = currentItem.periodType === "CUSTOM"
      ? Math.max(1, Number(currentItem.periodDays || 1))
      : (fixedPeriodDays[currentItem.periodType] || 1);

    const normalizedItems = (currentItem.resourcePlanItems || []).map((item) => {
      let positions = Array.isArray(item.positions)
        ? item.positions.filter((position) => Number(position?.id || 0) > 0)
        : [];
      if (positions.length === 0 && !item.allOtherPositions && Number(item.position?.id || 0) > 0) {
        positions = [item.position];
      }
      const uniquePositions = Array.from(new Map(positions.map((position) => [Number(position.id), position])).values());
      return {
        ...item,
        id: Number(item.id || 0) > 0 ? Number(item.id) : null,
        dayIndex: Math.min(Math.max(1, Number(item.dayIndex || 1)), maxDayIndex),
        department: Number(item.department?.id || 0) > 0 ? item.department : null,
        positions: uniquePositions,
        position: null,
        allOtherPositions: false,
        requiredPeopleCount: item.requiredPeopleCount === "" || item.requiredPeopleCount == null
          ? null
          : Number(item.requiredPeopleCount),
        priority: item.priority === "" || item.priority == null ? null : Number(item.priority),
        workStart: formatToLocalTime(item.workStart) || planWorkStart,
        workEnd: formatToLocalTime(item.workEnd) || planWorkEnd
      };
    });

    const totalStaff = normalizedItems.reduce((sum, item) => sum + Number(item.requiredPeopleCount || 0), 0);
    const totalMinutes = normalizedItems.reduce((sum, item) => {
      if (!item.workStart || !item.workEnd) return sum;
      const [startH, startM] = item.workStart.split(":").map(Number);
      const [endH, endM] = item.workEnd.split(":").map(Number);
      let duration = (endH * 60 + endM) - (startH * 60 + startM);
      if (duration <= 0) duration += 24 * 60;
      return sum + duration * Number(item.requiredPeopleCount || 0);
    }, 0);

    return {
      ...currentItem,
      periodType: currentItem.periodType || "DAILY",
      periodDays: maxDayIndex,
      workStart: planWorkStart,
      workEnd: planWorkEnd,
      resourcePlanItems: normalizedItems,
      totalStaff,
      totalHours: `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`
    };
  };

  const validatePreparedPlan = (prepared) => {
    if (!prepared.name?.trim() || !prepared.shortName?.trim()) {
      return "Uzupełnij nazwę i skróconą nazwę planu.";
    }
    if (!prepared.workStart || !prepared.workEnd) {
      return "Uzupełnij domyślny początek i koniec pracy dla planu.";
    }
    if (prepared.workStart === prepared.workEnd) {
      return "Domyślny początek i koniec pracy nie mogą być takie same.";
    }

    const poolsByKey = new Map();
    for (const item of prepared.resourcePlanItems) {
      if (!item.department?.id) return `Wybierz dział dla pozycji: ${describePlanItem(item)}.`;
      if (!item.requiredPeopleCount || item.requiredPeopleCount <= 0) return `Liczba osób musi być większa od zera: ${describePlanItem(item)}.`;
      if (item.priority == null || item.priority < 0) return `Priorytet musi być liczbą nieujemną: ${describePlanItem(item)}.`;
      if (!item.workStart || !item.workEnd) return `Brak godzin pracy: ${describePlanItem(item)}.`;
      if (item.workStart === item.workEnd) return `Początek i koniec pracy nie mogą być takie same: ${describePlanItem(item)}.`;

      const ids = new Set((item.positions || []).map((position) => Number(position.id)).filter((id) => id > 0));
      const key = `${item.department.id}:${item.dayIndex}:${item.workStart}:${item.workEnd}`;
      const existingPools = poolsByKey.get(key) || [];
      for (const existing of existingPools) {
        const overlaps = existing.ids.size === 0 || ids.size === 0 || [...ids].some((id) => existing.ids.has(id));
        if (overlaps) {
          return `Nakładające się pule stanowisk: ${describePlanItem(existing.item)} oraz ${describePlanItem(item)}. Połącz je w jedną pozycję albo usuń wspólne stanowisko.`;
        }
      }
      existingPools.push({ids, item});
      poolsByKey.set(key, existingPools);
    }
    return null;
  };

  const buildRequestPayload = (prepared, adding) => {
    const payloadItems = prepared.resourcePlanItems.map((item) => {
      const payload = {
        ...item,
        positions: (item.positions || []).map((position) => ({id: position.id})),
        department: item.department ? {id: item.department.id} : null
      };
      if (!payload.id) delete payload.id;
      if (adding) {
        delete payload.createdAt;
        delete payload.updatedAt;
        delete payload.createdBy;
        delete payload.updatedBy;
      }
      return payload;
    });

    const payload = {
      ...prepared,
      resourcePlanItems: payloadItems,
      createdBy: adding ? auth.id : prepared.createdBy,
      updatedBy: auth.id
    };

    if (adding) {
      delete payload.id;
      delete payload.createdAt;
      delete payload.updatedAt;
    }
    return payload;
  };

  const onSaveItemClick = async (e) => {
    e.preventDefault();
    setSaveFeedback(null);

    const prepared = preparePlanForSave();
    const validationError = validatePreparedPlan(prepared);
    if (validationError) {
      showSaveError(`Nie zapisano planu zapotrzebowania. ${validationError}`);
      return;
    }

    const adding = currentFormState.formAddingDataMode;
    const payload = buildRequestPayload(prepared, adding);
    let response;

    try {
      response = adding
        ? await createItem(dataUrl, payload, state)
        : await updateItem(`${dataUrl}/${prepared.id}`, payload, state);
    } catch (error) {
      showSaveError("Nie zapisano planu zapotrzebowania. Nie udało się połączyć z serwerem.");
      return;
    }

    if (response?.status === 401 || response?.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
      return;
    }

    if (!response || response.status < 200 || response.status >= 300) {
      const message = responseMessage(response);
      const statusText = response?.status ? ` Błąd HTTP ${response.status}.` : "";
      if (response?.status === 409) setUpdateConflict(true);
      showSaveError(`Nie zapisano planu zapotrzebowania.${statusText}${message ? ` ${message}` : " Sprawdź dane i spróbuj ponownie."}`);
      return;
    }

    const savedItem = response.data;
    if (!savedItem || typeof savedItem !== "object") {
      showSaveError("Serwer potwierdził zapis, ale nie zwrócił danych zapisanego planu. Odśwież listę przed dalszą edycją.");
      return;
    }

    setCurrentItem(savedItem);
    setBackupItem(structuredClone(savedItem));
    setCurrentFormState((prev) => ({...prev, formSaveButtonDisabled: true, formAddingDataMode: false}));
    setSaveFeedback({type: "success", message: "Plan zapotrzebowania został zapisany."});
    toastSuccess("Plan zapotrzebowania został zapisany.");
  };

  const checkRelatedItems = async () => {
    setAllowDelete(true);
  };

  const onClose = () => {
    setUpdateConflict(false);
    setSaveFeedback(null);
    onCloseDetails({state});
  };

  useEffect(() => {
    setWorkStart(currentItem.workStart ? parseTimeToDate(currentItem.workStart) : null);
    setWorkEnd(currentItem.workEnd ? parseTimeToDate(currentItem.workEnd) : null);
  }, [currentItem.workStart, currentItem.workEnd]);

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  useEffect(() => {
    const getData = async () => {
      const [itemsData, positionsData, departmentsData] = await Promise.all([
        getItems(dataUrl),
        getItems(positionsUrl),
        getItems(departmentsUrl)
      ]);

      if (itemsData?.status === 200 && positionsData?.status === 200 && departmentsData?.status === 200) {
        setItems(itemsData.data);
        setPositionsList(positionsData.data);
        setDepartmentsList(departmentsData.data);
        setLoading(false);
        return;
      }

      if ([itemsData?.status, positionsData?.status, departmentsData?.status].some((status) => status === 401 || status === 403)) {
        navigate('/login', {state: {from: location}, replace: true});
        return;
      }

      toastError("Nie udało się pobrać danych potrzebnych do planu zapotrzebowania.");
      setLoading(false);
    };

    getData();
  }, []);

  const getCompleteItem = async (id) => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response?.status === 200) return response.data;
    if (response?.status === 401 || response?.status === 403) {
      navigate('/login', {state: {from: location}, replace: true});
      return null;
    }
    toastError("Nie udało się pobrać planu zapotrzebowania.");
    return null;
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "Tu możesz dodać nowy element.",
    formHeader: "Nowy element",
    buttonTitle: "Dodaj nowy Plan zapotrzebowania"
  };

  const handlePeriodTypeChange = (event) => {
    const periodType = event.target.value;
    setCurrentItem((prev) => {
      const periodDays = periodType === "CUSTOM"
        ? Math.max(1, Number(prev.periodDays || 1))
        : (fixedPeriodDays[periodType] || 1);
      const resourcePlanItems = (prev.resourcePlanItems || []).map((item) => ({
        ...item,
        dayIndex: Math.min(Math.max(1, Number(item.dayIndex || 1)), periodDays)
      }));
      return {...prev, periodType, periodDays, resourcePlanItems};
    });
    setItemChanged((prev) => !prev);
  };

  const handlePeriodDaysChange = (event) => {
    const periodDays = Math.min(366, Math.max(1, Number(event.target.value || 1)));
    setCurrentItem((prev) => ({
      ...prev,
      periodDays,
      resourcePlanItems: (prev.resourcePlanItems || []).map((item) => ({
        ...item,
        dayIndex: Math.min(Math.max(1, Number(item.dayIndex || 1)), periodDays)
      }))
    }));
    setItemChanged((prev) => !prev);
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={checkRelatedItems}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        getCompleteItem={getCompleteItem}
        tableClassName="resource-list-table resource-crud-table"
        tableContainerClassName="resource-list-table-scroll"
      />
    );
  } else {
    dataSectionContent = (
      <div className="warn_info">
        <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>&nbsp;&nbsp;&nbsp;NIE ZNALEZIONO ŻADNYCH DANYCH
      </div>
    );
  }

  return (
    <div id="layoutSidenav_content" className="resource-page">
      <ToastContainer/>
      {currentFormState.showForm ? (
        <AppComponentCardHeader
          title={currentFormState.formAddingDataMode
            ? `Nowy plan zapotrzebowania${currentItem?.name ? ` — ${currentItem.name}` : ""}`
            : `Edycja planu zapotrzebowania${currentItem?.name ? ` — ${currentItem.name}` : ""}`}
        />
      ) : (
        <AppComponentCardHeader
          title={`Pozycje słownika ${itemNames.charAt(0).toUpperCase()}${itemNames.slice(1).toLowerCase()}`}
          buttonProps={addDataButtonProps}
        />
      )}

      <div className="container-fluid px-4 py-3">
        {currentFormState.showForm ? (
          <div className="custom-modal shadow rounded resource-plan-editor" id="modal_wrapper">
            <div className="custom-modal-body">
              <section className="mb-4">
                {saveFeedback && (
                  <div
                    id="resource-plan-save-feedback"
                    className={`alert alert-${saveFeedback.type} mb-3 resource-plan-save-feedback`}
                    role="alert"
                  >
                    {saveFeedback.message}
                  </div>
                )}

                <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
                  <p className="mb-0 data_changed" id="data-changed">
                    <FontAwesomeIcon icon={faExclamationCircle}/>&nbsp;{currentFormState.formDataChangedWarning}
                  </p>
                  <Button
                    variant="secondary"
                    id="btn-restore"
                    className="btn-restore"
                    onClick={() => {
                      setUpdateConflict(false);
                      setSaveFeedback(null);
                      restoreFormData({state});
                    }}
                  >
                    Wyczyść zmiany
                  </Button>
                </div>

                <form id="add-resourcePlan-form" name="add-resourcePlan-form">
                  <div className="resource-plan-section">
                    <h5 className="resource-plan-section-title">Dane planu</h5>
                    <div className="resource-plan-meta-grid">
                      <div className="resource-plan-field resource-plan-field-wide">
                        <label htmlFor="name">Nazwa <span className="required">*</span></label>
                        <TextInput propertyName="name" required={true} state={state}/>
                      </div>

                      <div className="resource-plan-field">
                        <label htmlFor="shortName">Skrót <span className="required">*</span></label>
                        <TextInput propertyName="shortName" maxlen="10" required={true} state={state}/>
                      </div>

                      <div className="resource-plan-field resource-plan-field-small">
                        <label htmlFor="active">Aktywny</label>
                        <Toggler propertyName="active" state={state}/>
                      </div>

                      <div className="resource-plan-field resource-plan-field-full">
                        <label htmlFor="description">Opis</label>
                        <TextArea propertyName="description" required={false} rows="2" state={state}/>
                      </div>

                      <div className="resource-plan-field">
                        <label htmlFor="periodType">Okres planu <span className="required">*</span></label>
                        <select id="periodType" className="form-select" value={currentItem.periodType || "DAILY"} onChange={handlePeriodTypeChange}>
                          {Object.entries(periodTypeLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>

                      {currentItem.periodType === "CUSTOM" && (
                        <div className="resource-plan-field resource-plan-field-small">
                          <label htmlFor="periodDays">Liczba dni <span className="required">*</span></label>
                          <input
                            id="periodDays"
                            type="number"
                            min="1"
                            max="366"
                            className="form-control"
                            value={currentItem.periodDays || 1}
                            onChange={handlePeriodDaysChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="resource-plan-section">
                    <h5 className="resource-plan-section-title">Domyślne godziny i podsumowanie</h5>
                    <div className="resource-plan-meta-grid">
                      <div className="resource-plan-field">
                        <label htmlFor="workStart"><span className="required">*</span> Początek pracy</label>
                        <QTimePicker
                          pickerId="workStart"
                          errorParagraphId="wrongWorkStartTimeFormat"
                          state={state}
                          propertyName="workStart"
                          startTime={workStart}
                          setStartTime={setWorkStart}
                          caption="Godzina rozpoczęcia pracy"
                        />
                        <p id="wrongWorkStartTimeFormat" className="err_info offscreen">
                          <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Nieprawidłowy format czasu
                        </p>
                      </div>

                      <div className="resource-plan-field">
                        <label htmlFor="workEnd"><span className="required">*</span> Koniec pracy</label>
                        <QTimePicker
                          pickerId="workEnd"
                          errorParagraphId="wrongWorkEndTimeFormat"
                          state={state}
                          propertyName="workEnd"
                          startTime={workEnd}
                          setStartTime={setWorkEnd}
                          caption="Godzina zakończenia pracy"
                        />
                        <p id="wrongWorkEndTimeFormat" className="err_info offscreen">
                          <FontAwesomeIcon icon={faInfoCircle}/>&nbsp; Nieprawidłowy format czasu
                        </p>
                      </div>

                      <div className="resource-plan-field resource-plan-field-small resource-plan-summary-value">
                        <label htmlFor="totalStaff">Łączna liczba osób</label>
                        <TextInput propertyName="totalStaff" disabled={true} state={state}/>
                      </div>

                      <div className="resource-plan-field resource-plan-field-small resource-plan-summary-value">
                        <label htmlFor="totalHours">Łączna liczba godzin</label>
                        <TextInput propertyName="totalHours" disabled={true} state={state}/>
                      </div>
                    </div>
                    <div className="small text-muted mt-2">
                      Godziny z tego miejsca są domyślne dla nowych pozycji i uzupełniają pozycje, w których nie wpisano indywidualnych godzin.
                    </div>
                  </div>

                  <ResourcePlanItemsCard
                    state={state}
                    positionsList={positionsList}
                    departmentsList={departmentsList}
                  />

                  <p id="conflict_notification" className={updateConflict ? "err_info mt-3" : "offscreen"}>
                    <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;
                    Nie można dodać ani zmienić tego planu, ponieważ istnieje już plan o tej nazwie lub skrócie.
                  </p>
                </form>
              </section>
            </div>

            <ModalControlsSaveOnly
              onDelete={onDelete}
              onCloseDetails={onClose}
              onSubmit={onSaveItemClick}
              state={state}
            />
          </div>
        ) : (
          <div className="card shadow-sm mb-5 resource-list-card resource-standard-list-card">
            {dataSectionContent}
          </div>
        )}
      </div>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default ResourcePlan;
